/** @file Topology actions on a mesh (tool keys and Weights-panel buttons): the batch to commit, or a hint. */
import { meshOffsets } from '@pose2d/commands/animation/meshOffsets';
import type { CommandCall, Doc } from '@pose2d/format/types';
import type { CatalogKey } from '@pose2d/i18n/types';
import type { MeshTarget } from './meshTarget';
import type { TopologyPlan } from './topology/blendSources';
import { deleteVerticesPlan } from './topology/deleteVerticesPlan';
import { outlineHullPlan } from './topology/outlineHullPlan';
import { retriangulate } from './topology/retriangulate';
import { topologyCalls } from './topology/topologyCalls';
import { triangleEdits } from './topology/triangleEdits';

/** Every mesh action the tool and the panel offer. */
export type MeshActionId = 'deleteVertices' | 'retriangulate' | 'outlineHull' | 'flipEdge' | 'addTriangle' | 'removeTriangle';

/** One batch to commit and the vertex selection afterwards, or why nothing happens. */
export type MeshActionOutcome = { calls: CommandCall[]; select: number[] } | { hint: CatalogKey };

type Handler = (doc: Doc, t: MeshTarget, selected: readonly number[]) => MeshActionOutcome;

const hint = (key: CatalogKey): MeshActionOutcome => ({ hint: key });

/** New triangles (same vertices and hull) as a batch; `missing` when the edit did not apply. */
function withTriangles(doc: Doc, t: MeshTarget, triangles: number[] | null, select: readonly number[], missing: CatalogKey): MeshActionOutcome {
  if (triangles === null) return hint(missing);
  const count = t.mesh.uvs.length / 2;
  const plan: TopologyPlan = { sources: Array.from({ length: count }, (_, v) => ({ kind: 'old', v })), triangles, hull: t.mesh.hull, edges: t.mesh.edges ?? null };
  return { calls: topologyCalls(doc, t, plan), select: [...select] };
}

/** A plan that renumbers vertices: committed with an empty vertex selection (old indices are stale). */
const renumbered = (doc: Doc, t: MeshTarget, plan: TopologyPlan): MeshActionOutcome => ({ calls: topologyCalls(doc, t, plan), select: [] });

const HANDLERS: Record<MeshActionId, Handler> = {
  deleteVertices(doc, t, selected) {
    if (selected.length === 0) return hint('tools.editMesh.selectVertices');
    const plan = deleteVerticesPlan(t.mesh, selected);
    if (plan === 'hullTooSmall') return hint('tools.editMesh.hullTooSmall');
    return plan === 'noTriangles' ? hint('tools.editMesh.topologyFailed') : renumbered(doc, t, plan);
  },
  retriangulate(doc, t, selected) {
    const r = retriangulate(meshOffsets.rest(t.mesh), t.mesh.hull);
    return withTriangles(doc, t, r?.triangles ?? null, selected, 'tools.editMesh.topologyFailed');
  },
  outlineHull(doc, t) {
    const plan = outlineHullPlan(t.mesh);
    return plan === null ? hint('tools.editMesh.noOutline') : renumbered(doc, t, plan);
  },
  flipEdge(doc, t, [a, b, ...more]) {
    if (a === undefined || b === undefined || more.length > 0) return hint('tools.editMesh.selectTwo');
    return withTriangles(doc, t, triangleEdits.flip(t.mesh.triangles, meshOffsets.rest(t.mesh), a, b), [a, b], 'tools.editMesh.noEdge');
  },
  addTriangle(doc, t, [a, b, c, ...more]) {
    if (a === undefined || b === undefined || c === undefined || more.length > 0) return hint('tools.editMesh.selectThree');
    return withTriangles(doc, t, triangleEdits.add(t.mesh.triangles, meshOffsets.rest(t.mesh), a, b, c), [a, b, c], 'tools.editMesh.triangleExists');
  },
  removeTriangle(doc, t, [a, b, c, ...more]) {
    if (a === undefined || b === undefined || c === undefined || more.length > 0) return hint('tools.editMesh.selectThree');
    return withTriangles(doc, t, triangleEdits.remove(t.mesh.triangles, a, b, c), [a, b, c], 'tools.editMesh.noTriangle');
  },
};

/** Runs action `id` on the target with the selected vertex indices (Setup data: callers refuse it in Animate mode). */
export function meshAction(id: MeshActionId, doc: Doc, target: MeshTarget, selected: readonly number[]): MeshActionOutcome {
  return HANDLERS[id](doc, target, selected);
}
